/**
 * Onboarding Steps Redux Slice
 *
 * Manages state for onboarding steps data fetched from the API.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { OnboardingStep } from '@/types/onboarding.types';

interface OnboardingStepsState {
  steps: OnboardingStep[];
  isLoading: boolean;
  lastFetchedThreadId: string | null;
  lastFetchTime: number | null;
  subdomain: string | null;
}

const initialState: OnboardingStepsState = {
  steps: [],
  isLoading: false,
  lastFetchedThreadId: null,
  lastFetchTime: null,
  subdomain: null,
};

const onboardingStepsSlice = createSlice({
  name: 'onboardingSteps',
  initialState,
  reducers: {
    /**
     * Update the steps array with new data
     */
    setSteps: (state, action: PayloadAction<OnboardingStep[]>) => {
      state.steps = action.payload;
      state.lastFetchTime = Date.now();
    },

    /**
     * Update the loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Track which thread was last fetched
     */
    setLastFetchedThreadId: (state, action: PayloadAction<string | null>) => {
      state.lastFetchedThreadId = action.payload;
    },

    /**
     * Update the subdomain
     */
    setSubdomain: (state, action: PayloadAction<string | null>) => {
      state.subdomain = action.payload;
    },

    /**
     * Reset state (used when thread changes to null or switches)
     */
    resetSteps: (state) => {
      state.steps = [];
      state.lastFetchedThreadId = null;
      state.lastFetchTime = null;
      state.isLoading = false;
      state.subdomain = null;
    },
  },
});

export const { setSteps, setLoading, setLastFetchedThreadId, setSubdomain, resetSteps } =
  onboardingStepsSlice.actions;

export default onboardingStepsSlice.reducer;
